import React from 'react';
import { Link } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faPlus, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { PaginationControl } from 'react-bootstrap-pagination-control';
import { Zigzag, ZigzagStrip } from '../../Themes/Brand';

interface TableShellProps {
    title: string;
    icon: IconDefinition;
    accentColor: string;
    backHref?: string;
    createHref?: string;
    createLabel?: string;
    isLoading: boolean;
    isEmpty: boolean;
    emptyMessage?: string;
    toolbar?: React.ReactNode;
    children?: React.ReactNode;
    page?: number;
    totalCount?: number;
    perPage?: number;
    onPageChange?: (p: number) => void;
}

const TableShell: React.FC<TableShellProps> = ({
    title,
    icon,
    accentColor,
    backHref = '/home',
    createHref,
    createLabel = 'Vytvoriť',
    isLoading,
    isEmpty,
    emptyMessage = 'Žiadne záznamy',
    toolbar,
    children,
    page,
    totalCount,
    perPage,
    onPageChange,
}) => {
    const hasPagination =
        !isLoading &&
        !isEmpty &&
        page !== undefined &&
        totalCount !== undefined &&
        perPage !== undefined &&
        onPageChange !== undefined;

    return (
        <Container fluid className="py-3 st-table-shell-container">
            <div
                className="st-table-shell-card"
                style={{ '--shell-accent': accentColor, '--shell-icon-tint': accentColor + '1A' } as React.CSSProperties}
            >
                {/* The signature: every card carries the stabilimentum in its own dye */}
                <ZigzagStrip color={accentColor} />

                {/* Header */}
                <div className="st-table-shell-header">
                    <div className="st-table-shell-header-left">
                        <Link to={backHref} title="Späť" className="st-btn st-btn--outline st-btn--icon st-btn--sm">
                            <FontAwesomeIcon icon={faArrowLeft} className="st-table-shell-back-icon" />
                        </Link>
                        <div className="st-table-shell-icon-badge">
                            <FontAwesomeIcon icon={icon} className="st-table-shell-icon" />
                        </div>
                        <h2 className="st-table-shell-title">
                            {title}
                        </h2>
                    </div>

                    {createHref && (
                        <Link
                            to={createHref}
                            className="st-btn st-btn--fill"
                            style={{ '--btn-accent': accentColor } as React.CSSProperties}
                        >
                            <FontAwesomeIcon icon={faPlus} className="st-table-shell-create-icon" />
                            {createLabel}
                        </Link>
                    )}
                </div>

                {/* Toolbar: count + list controls */}
                {toolbar && !isLoading && (
                    <div className="st-table-shell-toolbar">
                        {toolbar}
                    </div>
                )}

                {/* Body */}
                {isLoading ? (
                    <SkeletonRows />
                ) : isEmpty ? (
                    <div className="st-table-shell-empty-wrap">
                        <EmptyState icon={icon} accentColor={accentColor} message={emptyMessage} />
                    </div>
                ) : (
                    <div className="table-modern st-table-shell-body">
                        {children}
                    </div>
                )}

                {/* Pagination */}
                {hasPagination && (
                    <div className="table-shell-pagination">
                        <PaginationControl
                            page={page!}
                            between={4}
                            total={totalCount!}
                            limit={perPage!}
                            changePage={onPageChange!}
                            ellipsis={1}
                        />
                    </div>
                )}
            </div>
        </Container>
    );
};

const SkeletonRows: React.FC = () => (
    <div className="st-table-shell-skeleton">
        {[0.85, 0.7, 0.55, 0.4].map((opacity, i) => (
            <div key={i} className="st-table-shell-skeleton-bar" style={{ opacity }} />
        ))}
    </div>
);

const EmptyState: React.FC<{ icon: IconDefinition; accentColor: string; message: string }> = ({ icon, accentColor, message }) => (
    <div className="st-table-shell-empty">
        <div className="st-table-shell-empty-icon">
            <FontAwesomeIcon icon={icon} />
        </div>
        {/* an undecorated web: the mark waits for content */}
        <Zigzag color={accentColor + '66'} width={44} height={9} style={{ marginBottom: '0.7rem' }} />
        <p className="st-table-shell-empty-message">{message}</p>
    </div>
);

export default TableShell;
